import {
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
    type CSSProperties,
    type KeyboardEvent,
    type ReactElement,
} from "react";
import { space } from "@tokens/index";
import { Popover } from "@primitives/overlay";
import { Text } from "@primitives/Text";
import { TextInput } from "@primitives/TextInput";
import type { UserSearchResult } from "../../../experience-shell/queues";
import { UserResultRow } from "./UserResultRow";

/**
 * A combobox over the HR user search. The input is the anchor; the result
 * list is a `Popover role="listbox"` that matches the input width. Arrow
 * keys move the active option, Enter picks the highlighted (or first)
 * result, Escape closes the list and consumes the key.
 *
 * A pick clears the query, closes the list and keeps focus in the input so
 * the next member can be typed at once. The input is never disabled while a
 * pick is in flight: disabling a focused input blurs it, which is the jump
 * the owner saw. The busy state is announced with `aria-busy` instead.
 */

export interface UserPickerProps {
    readonly searchTerm: string;
    readonly results: readonly UserSearchResult[];
    readonly isSearching: boolean;
    readonly error: Error | null;
    readonly isAdding: boolean;
    readonly excludedUserIds: ReadonlySet<string>;
    readonly onSearchTermChange: (term: string) => void;
    readonly onPick: (user: UserSearchResult) => void;
}

const resultEmptyStyle: CSSProperties = {
    padding: `${space[2]} ${space[3]}`,
};

export function UserPicker({
    searchTerm,
    results,
    isSearching,
    error,
    isAdding,
    excludedUserIds,
    onSearchTermChange,
    onPick,
}: UserPickerProps): ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const inputRef = useRef<HTMLInputElement>(null);
    const listId = useId();
    const visibleResults = useMemo(
        () => results.filter((r) => !excludedUserIds.has(r.id)),
        [results, excludedUserIds],
    );

    const showPanel = isOpen && searchTerm.trim().length >= 2;
    const hasOptions = error === null && !isSearching && visibleResults.length > 0;

    useEffect(() => {
        setActiveIndex(0);
    }, [visibleResults]);

    const close = useCallback(() => setIsOpen(false), []);

    const handlePick = useCallback(
        (user: UserSearchResult) => {
            onPick(user);
            onSearchTermChange("");
            setIsOpen(false);
            // A pointer pick keeps focus in the input (the list swallows
            // mousedown); only a pick from elsewhere needs the focus moved.
            const input = inputRef.current;
            if (input !== null && document.activeElement !== input) {
                input.focus({ preventScroll: true });
            }
        },
        [onPick, onSearchTermChange],
    );

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Escape") {
                if (!showPanel) return;
                e.preventDefault();
                e.stopPropagation();
                close();
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                if (!showPanel) {
                    setIsOpen(true);
                    return;
                }
                if (!hasOptions) return;
                setActiveIndex((i) => Math.min(i + 1, visibleResults.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                if (!hasOptions) return;
                setActiveIndex((i) => Math.max(i - 1, 0));
            } else if (e.key === "Enter") {
                if (!showPanel || !hasOptions) return;
                e.preventDefault();
                const user = visibleResults[activeIndex] ?? visibleResults[0];
                if (user !== undefined) handlePick(user);
            }
        },
        [showPanel, hasOptions, visibleResults, activeIndex, handlePick, close],
    );

    const activeOptionId = showPanel && hasOptions ? `${listId}-${String(activeIndex)}` : undefined;

    return (
        <div style={{ width: "100%", minWidth: 0 }}>
            <TextInput
                ref={inputRef}
                fullWidth
                type="search"
                autoComplete="off"
                role="combobox"
                aria-expanded={showPanel}
                aria-controls={listId}
                aria-autocomplete="list"
                aria-activedescendant={activeOptionId}
                aria-busy={isAdding || undefined}
                placeholder="Search users by name, email, or principal name…"
                aria-label="Search users to add to this queue"
                value={searchTerm}
                onFocus={() => setIsOpen(true)}
                onKeyDown={handleKeyDown}
                onChange={(e) => {
                    onSearchTermChange(e.target.value);
                    setIsOpen(true);
                }}
            />
            <Popover
                id={listId}
                open={showPanel}
                onClose={close}
                anchorRef={inputRef}
                matchAnchorWidth
                role="listbox"
                initialFocus="none"
                returnFocus={false}
                maxHeight={320}
            >
                <div style={{ padding: space[1] }} onMouseDown={(e) => e.preventDefault()}>
                    {error !== null && (
                        <div style={resultEmptyStyle}>
                            <Text role="bodySm" tone="danger">
                                Couldn&rsquo;t search users. {error.message}
                            </Text>
                        </div>
                    )}
                    {error === null && isSearching && (
                        <div style={resultEmptyStyle} role="status">
                            <Text role="bodySm" tone="muted">
                                Searching…
                            </Text>
                        </div>
                    )}
                    {error === null && !isSearching && visibleResults.length === 0 && (
                        <div style={resultEmptyStyle}>
                            <Text role="bodySm" tone="muted">
                                {results.length === 0
                                    ? "No users found."
                                    : "All matches are already members."}
                            </Text>
                        </div>
                    )}
                    {hasOptions &&
                        visibleResults.map((user, index) => (
                            <UserResultRow
                                key={user.id}
                                id={`${listId}-${String(index)}`}
                                role="option"
                                aria-selected={index === activeIndex}
                                tabIndex={-1}
                                user={user}
                                active={index === activeIndex}
                                onMouseEnter={() => setActiveIndex(index)}
                                onClick={() => handlePick(user)}
                            />
                        ))}
                </div>
            </Popover>
        </div>
    );
}
